<template>
  <v-dialog v-model="dialogModifMasse" fullscreen>
    <v-card>
      <v-toolbar dark color="primary">
        <v-btn icon dark @click="dialogModifMasse = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title>Modifications de masse</v-toolbar-title>
      </v-toolbar>
      <v-card-text class="pa-5">
        <p>Vous allez modifier en masse les {{ count }} {{ libelleEntite }}.</p>

        <dialogRechercheGestionnaire
          ref="dialogRechercheGestionnaire"
          :subtitle="
            $t('instance.detail.form.gestionnaires.dialog.description')
          "
          @input="addGestionnaire"
        />

        <v-stepper v-model="step" vertical>
          <v-stepper-step step="1">
            Sélectionner les champs à modifier
          </v-stepper-step>

          <v-stepper-content step="1" :complete="step > 1">
            <p>
              Les champs vides ne seront pas modifiés. Veuillez sélectionner au
              moins un champ pour continuer.
            </p>
            <template v-if="modifContact">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="contact.type"
                    :items="enums.typeContact"
                    item-text="libelle"
                    item-value="id"
                    return-object
                    filled
                    clearable
                    hide-details
                    :label="
                      $t('personnePhysique.detail.form.contact.typeContact')
                    "
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    v-model="contact.fonction"
                    :items="enums.fonctionContact"
                    item-text="libelle"
                    item-value="id"
                    return-object
                    filled
                    clearable
                    hide-details
                    :label="$t('personnePhysique.detail.form.contact.fonction')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-select
                    clearable
                    filled
                    return-object
                    :items="[
                      { value: 'MAIL', libelle: 'Mail' },
                      { value: 'TELEPHONE', libelle: 'Téléphone' },
                    ]"
                    v-model="contact.moyenContactPrefere"
                    item-text="libelle"
                    :label="
                      $t(
                        'personnePhysique.detail.form.contact.moyenContactPrefere',
                      )
                    "
                    hide-details
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    ref="menuDateLimite"
                    v-model="menuValidite"
                    :close-on-content-click="false"
                    :return-value.sync="contact.dateLimiteValidite"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateFormatted"
                        dense
                        filled
                        readonly
                        clearable
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        prepend-inner-icon="event"
                        :label="
                          $t(
                            'personnePhysique.detail.form.contact.dateLimiteValidite',
                          )
                        "
                      />
                    </template>
                    <v-card flat>
                      <v-date-picker
                        v-model="contact.dateLimiteValidite"
                        no-title
                        scrollable
                      />

                      <v-divider />
                      <v-card-actions>
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.menuDateLimite.save(
                              contact.dateLimiteValidite,
                            )
                          "
                          >OK</v-btn
                        >
                        <v-btn text @click="menuValidite = false"
                          >Annuler</v-btn
                        >
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                </v-col>

                <v-col cols="12">
                  <v-btn
                    color="primary"
                    :disabled="contactDisabled"
                    @click="continuerModif"
                  >
                    Continuer</v-btn
                  >
                </v-col>
              </v-row>
            </template>

            <template v-if="modifPersonneMorale">
              <v-row dense>
                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-checkbox
                      class="ml-3 ma-1 pa-0"
                      hide-details
                      v-model="personneMorale.utiliseContexte"
                    />
                    <v-select
                      return-object
                      filled
                      multiple
                      chip
                      v-model="personneMorale.contextes"
                      :items="enums.contexte"
                      prepend-inner-icon="mdi-layers-triple"
                      item-text="libelle"
                      item-value="id"
                      hide-details
                      :disabled="!personneMorale.utiliseContexte"
                      :label="$t('personneMorale.detail.form.contextes')"
                    >
                      <v-chip small color="secondary" #selection="{item}"
                        >>
                        {{ item.libelle }}
                      </v-chip>
                    </v-select>
                  </div>
                </v-col>

                <v-col cols="12">
                  <div class="d-flex align-center">
                    <v-checkbox
                      class="ml-3 ma-1 pa-0"
                      hide-details
                      v-model="personneMorale.utiliseType"
                    />
                    <v-select
                      v-model="personneMorale.type"
                      :items="enums.typePersonneMorale"
                      item-text="libelle"
                      item-value="id"
                      return-object
                      filled
                      clearable
                      hide-details
                      :disabled="!personneMorale.utiliseType"
                      :label="$t('personneMorale.detail.form.type')"
                    />
                  </div>
                </v-col>

                <v-col cols="12">
                  <v-btn
                    color="primary"
                    :disabled="personneMoraleDisabled"
                    @click="continuerModif"
                  >
                    Continuer</v-btn
                  >
                </v-col>
              </v-row>
            </template>

            <template v-if="modifInstance">
              <v-row dense>
                <v-col cols="12">
                  <v-card flat>
                    <v-card-title>
                      {{ $tc('entetes.gestionnaire', 2) }}
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        small
                        outlined
                        color="secondary"
                        @click="selectGestionnaire"
                        >{{ $t('instance.detail.form.gestionnaires.add') }}
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <v-list dense>
                        <v-list-item v-if="instance.gestionnaires.length === 0">
                          <v-list-item-content>
                            Aucun gestionnaire
                          </v-list-item-content>
                        </v-list-item>
                        <template
                          v-for="(gestionnaire,
                          index) in instance.gestionnaires"
                        >
                          <v-list-item :key="`gestionnaires${index}`">
                            <v-list-item-content>
                              <v-list-item-title>
                                {{ gestionnaire.libelle }}
                              </v-list-item-title>
                            </v-list-item-content>

                            <v-list-item-action>
                              <v-btn
                                small
                                icon
                                @click.stop="removeGestionnaire(index)"
                              >
                                <v-icon>mdi-account-remove</v-icon>
                              </v-btn>
                            </v-list-item-action>
                          </v-list-item>

                          <v-divider
                            v-if="index < instance.gestionnaires.length - 1"
                            :key="`divider${index}`"
                          />
                        </template>
                      </v-list>
                    </v-card-text>
                  </v-card>
                </v-col>

                <v-col cols="12">
                  <v-btn
                    color="primary"
                    :disabled="instanceDisabled"
                    @click="continuerModif"
                  >
                    Continuer</v-btn
                  >
                </v-col>
              </v-row>
            </template>

            <template v-if="modifContactExpire">
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-menu
                    ref="menuDateLimite"
                    v-model="menuValidite"
                    :close-on-content-click="false"
                    :return-value.sync="contactExpire.dateLimiteValidite"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateFormatted"
                        dense
                        filled
                        readonly
                        clearable
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        prepend-inner-icon="event"
                        :label="
                          $t(
                            'personnePhysique.detail.form.contact.dateLimiteValidite',
                          )
                        "
                      />
                    </template>
                    <v-card flat>
                      <v-date-picker
                        v-model="contactExpire.dateLimiteValidite"
                        no-title
                        scrollable
                      />

                      <v-divider />
                      <v-card-actions>
                        <v-btn
                          text
                          color="primary"
                          @click="
                            $refs.menuDateLimite.save(
                              contactExpire.dateLimiteValidite,
                            )
                          "
                          >OK</v-btn
                        >
                        <v-btn text @click="menuValidite = false"
                          >Annuler</v-btn
                        >
                      </v-card-actions>
                    </v-card>
                  </v-menu>
                </v-col>

                <v-col cols="12" md="6"></v-col>

                <v-col cols="12">
                  <v-btn
                    color="primary"
                    :disabled="contactExpireDisabled"
                    @click="continuerModif"
                  >
                    Continuer</v-btn
                  >
                </v-col>
              </v-row>
            </template>
          </v-stepper-content>

          <v-stepper-step step="2">
            Valider la modification
          </v-stepper-step>

          <v-stepper-content step="2">
            <v-row>
              <v-col cols="12">
                <p>
                  Vous allez valoriser les champs
                  <template v-for="(r, i) in recapitulatifModifs">
                    <template v-if="i > 0">, </template>
                    {{ r.type }} =
                    <strong :key="`strong${i}`">{{ r.valeur }}</strong>
                  </template>
                  pour les {{ count }} {{ libelleEntite }}.
                </p>

                <p>
                  Confirmer vous la modification ?
                </p>
              </v-col>
              <v-col cols="12">
                <v-btn
                  color="primary"
                  @click="confirmerModification"
                  :loading="modificationEnCours"
                >
                  Continuer
                </v-btn>
                <v-btn text @click="annulerModification">
                  Non
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
      <v-card-actions> </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import toasted from '@/utils/toasted';
export default {
  data() {
    return {
      dialogModifMasse: false,
      menuValidite: false,
      contact: {
        type: null,
        fonction: null,
        moyenContactPrefere: null,
        dateLimiteValidite: null,
      },
      personneMorale: {
        utiliseContexte: false,
        utiliseType: false,
        contextes: [],
        type: null,
      },
      instance: {
        gestionnaires: [],
      },
      contactExpire: {
        dateLimiteValidite: null,
        pagination: null,
        selected: [],
      },
      modificationEnCours: false,
      type: null,
      rechercheAvancee: null,
      enums: {
        fonctionContact: [],
        typeContact: [],
        typePersonneMorale: [],
        contexte: [],
      },
      count: 0,
      step: 1,
    };
  },
  computed: {
    modifContact() {
      return this.type === 0;
    },
    modifPersonneMorale() {
      return this.type === 1;
    },
    modifInstance() {
      return this.type === 2;
    },
    modifContactExpire() {
      return this.type === 3;
    },
    libelleEntite() {
      if (this.type === 0) return 'contacts recherchés';
      else if (this.type === 1) return 'personnes morales recherchées';
      else if (this.type === 2) return 'instances recherchées';
      else return 'contacts expirés sélectionnés';
    },
    dateISO() {
      if (this.modifContact) {
        return this.contact.dateLimiteValidite;
      } else {
        return this.contactExpire.dateLimiteValidite;
      }
    },
    dateFormatted() {
      return this.formatDate(this.dateISO);
    },
    instanceDisabled() {
      return false;
    },
    contactDisabled() {
      return (
        !this.contact.type &&
        !this.contact.fonction &&
        !this.contact.moyenContactPrefere &&
        !this.contact.dateLimiteValidite
      );
    },
    contactExpireDisabled() {
      return !this.contactExpire.dateLimiteValidite;
    },
    personneMoraleDisabled() {
      return (
        !this.personneMorale.utiliseContexte &&
        !(this.personneMorale.type && this.personneMorale.utiliseType)
      );
    },
    recapitulatifModifs() {
      const recap = [];
      if (this.modifContact) {
        const contact = this.contact;
        if (contact.type) {
          recap.push({
            type: this.$t('personnePhysique.detail.form.contact.typeContact'),
            valeur: contact.type.libelle,
          });
        }

        if (contact.fonction) {
          recap.push({
            type: this.$t('personnePhysique.detail.form.contact.fonction'),
            valeur: contact.fonction.libelle,
          });
        }

        if (contact.moyenContactPrefere) {
          recap.push({
            type: this.$t(
              'personnePhysique.detail.form.contact.moyenContactPrefere',
            ),
            valeur: contact.moyenContactPrefere.libelle,
          });
        }

        if (contact.dateLimiteValidite) {
          recap.push({
            type: this.$t(
              'personnePhysique.detail.form.contact.dateLimiteValidite',
            ),
            valeur: this.formatDate(contact.dateLimiteValidite),
          });
        }
      } else if (this.modifPersonneMorale) {
        const personneMorale = this.personneMorale;
        if (personneMorale.utiliseContexte) {
          const valeur =
            personneMorale.contextes.length === 0
              ? 'Aucun contexte'
              : personneMorale.contextes.map(c => c.libelle).join(', ');
          recap.push({
            type: this.$t('personneMorale.detail.form.contextes'),
            valeur,
          });
        }

        if (personneMorale.utiliseType && personneMorale.type) {
          recap.push({
            type: this.$t('personneMorale.detail.form.type'),
            valeur: personneMorale.type.libelle,
          });
        }
      } else if (this.modifInstance) {
        const gestionnaires = this.instance.gestionnaires;
        const valeur =
          gestionnaires.length === 0
            ? 'Aucun gestionnaire'
            : gestionnaires.map(c => c.libelle).join(', ');
        recap.push({
          type: 'Gestionnaires',
          valeur,
        });
      } else if (this.modifContactExpire) {
        const contactExpire = this.contactExpire;

        if (contactExpire.dateLimiteValidite) {
          recap.push({
            type: this.$t(
              'personnePhysique.detail.form.contact.dateLimiteValidite',
            ),
            valeur: this.formatDate(contactExpire.dateLimiteValidite),
          });
        }
      }

      return recap;
    },
  },
  methods: {
    show(type, count, enums, rechercheAvancee) {
      this.dialogModifMasse = true;
      this.type = type;
      this.count = count;
      this.step = 1;
      this.modificationEnCours = false;

      this.contact.type = null;
      this.contact.fonction = null;
      this.contact.moyenContactPrefere = null;
      this.contact.dateLimiteValidite = null;

      this.personneMorale.utiliseContexte = false;
      this.personneMorale.utiliseType = false;
      this.personneMorale.contextes = [];
      this.personneMorale.type = null;

      this.rechercheAvancee = rechercheAvancee;

      this.enums.fonctionContact = enums.fonctionContact;
      this.enums.typeContact = enums.typeContact;
      this.enums.typePersonneMorale = enums.typePersonneMorale;
      this.enums.contexte = enums.contexte;
      this.enums.typeContactExpire = enums.typeContactExpire;
    },
    showModifMasseContactExpire(type, count, pagination, selected) {
      this.dialogModifMasse = true;
      this.type = type;
      this.count = count;
      this.step = 1;
      this.modificationEnCours = false;

      this.contactExpire.dateLimiteValidite = null;

      this.contactExpire.pagination = pagination;
      this.contactExpire.selected = selected;
    },
    addGestionnaire(personnePhysique) {
      this.instance.gestionnaires.push({
        id: personnePhysique.id,
        libelle: personnePhysique.nom + ' ' + personnePhysique.prenoms,
        idPersonnePhysique: personnePhysique.id,
      });
    },
    removeGestionnaire(index) {
      this.instance.gestionnaires.splice(index, 1);
    },
    selectGestionnaire() {
      this.$refs.dialogRechercheGestionnaire.show();
    },
    confirmerModification() {
      this.modificationEnCours = true;

      const recherche = JSON.parse(JSON.stringify(this.rechercheAvancee));
      var uri = null;
      const body = {
        recherche,
      };
      if (this.modifContact) {
        const contact = this.contact;
        if (contact.type) {
          body.type = contact.type.id;
        }

        if (contact.dateLimiteValidite) {
          body.dateLimiteValidite = contact.dateLimiteValidite;
        }

        if (contact.fonction) {
          body.fonction = contact.fonction.id;
        }

        if (contact.moyenContactPrefere) {
          body.moyenContactPrefere = contact.moyenContactPrefere.value;
        }

        uri = '/modif-masse/contacts';
      } else if (this.modifPersonneMorale) {
        const personneMorale = this.personneMorale;

        if (personneMorale.utiliseContexte) {
          body.contextes = personneMorale.contextes.map(c => c.id);
        }

        if (personneMorale.utiliseType) {
          body.type = personneMorale.type ? personneMorale.type.id : null;
        }

        uri = '/modif-masse/personnes-morales';
      } else if (this.modifInstance) {
        body.gestionnaires = this.instance.gestionnaires.map(c => c.id);
        uri = '/modif-masse/instances';
      } else if (this.modifContactExpire) {
        const ids = this.contactExpire.selected.flatMap(s => s.id);

        if (this.contactExpire.dateLimiteValidite) {
          var dateLimiteValidite = this.contactExpire.dateLimiteValidite;
        }

        var params = {
          dateLimiteValidite: dateLimiteValidite,
          ids: ids,
        };

        uri = '/modif-masse/contactsExpires';
      }

      if (!this.modifContactExpire) {
        this.$api(uri)
          .post(body)
          .then(() => {
            toasted.info('modficationmasse.status.valide');
            this.dialogModifMasse = false;
            this.$emit('valide');
          })
          .finally(() => (this.modificationEnCours = false));
      } else {
        this.$api(uri)
          .post(params)
          .then(() => {
            toasted.info('modficationmasse.status.valide');
            this.dialogModifMasse = false;
            this.$emit('valide');
          })
          .finally(() => (this.modificationEnCours = false));
      }
    },
    annulerModification() {
      this.step = 1;
    },
    continuerModif() {
      this.step = 2;
    },
    formatDate(isoDate) {
      if (!isoDate) return null;

      return moment(isoDate).format('DD/MM/YYYY');
    },
  },
};
</script>
